import { LoadingState, LoadingStates } from 'globals/types';
import {
  applySnapshot,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree';
import { getNumberOfDigits } from 'globals/utils';
import {
  IncomeOutgoingsSingleItem,
  IncomeOutgoingsSingleItemProps,
} from './income-outgoings-single-item';
import {
  BalancesSingleItem,
  BalancesSingleItemProps,
} from './balances-single-item';
import {
  CategorySingleItem,
  CategorySingleItemProps,
} from './category-single-item';
import {
  CategoryRowDataProps,
  mapCategories,
} from './bank-accounts-financial-stats.utils';

export interface BankAccountsFinancialStatsProps {
  incomes_outgoings: IncomeOutgoingsSingleItemProps[];
  historical_balances: BalancesSingleItemProps[];
  predicted_balances: BalancesSingleItemProps[];
  expenditure_categories: CategorySingleItemProps[];
  income_categories: CategorySingleItemProps[];
  status: LoadingState;
}

export interface IncomesOutgoingViewsSingleItemProps {
  x: number;
  income?: number;
  outgoing?: number;
  balance?: number;
}

export interface BalancesViewSingleItemProps {
  x: number;
  balance?: number;
  prediction?: number;
}

export const BankAccountsFinancialStats = types
  .model({
    incomes_outgoings: types.optional(
      types.array(IncomeOutgoingsSingleItem),
      []
    ),
    historical_balances: types.optional(types.array(BalancesSingleItem), []),
    predicted_balances: types.optional(types.array(BalancesSingleItem), []),
    expenditure_categories: types.optional(types.array(CategorySingleItem), []),
    income_categories: types.optional(types.array(CategorySingleItem), []),
    status: types.optional(
      types.enumeration(LoadingStates),
      LoadingState.INITIAL
    ),
  })
  .actions((self) => ({
    set(props: BankAccountsFinancialStatsProps): void {
      const currentSnapshot = getSnapshot(self);
      const newSnapshot = {
        ...currentSnapshot,
        ...props,
      };
      applySnapshot(self, newSnapshot);
    },
    updateStatus(status: LoadingState): void {
      const currentSnapshot = getSnapshot(self);
      const newSnapshot = {
        ...currentSnapshot,
        status,
      };
      applySnapshot(self, newSnapshot);
    },
  }))
  .views((self) => ({
    incomesOutgoings(): Array<IncomesOutgoingViewsSingleItemProps> {
      return self.incomes_outgoings.map(
        ({ date, outgoing, income, ...props }) => ({
          ...props,
          outgoing: outgoing !== 0 ? -1 * outgoing : undefined,
          income: income !== 0 ? income : undefined,
          x: getNumberOfDigits(date) < 12 ? date * 1000 : date,
        })
      );
    },
    balances(): Array<BalancesViewSingleItemProps> {
      return [
        ...self.historical_balances.map(({ date, amount }) => ({
          x: getNumberOfDigits(date) < 12 ? date * 1000 : date,
          balance: amount,
        })),
        ...self.predicted_balances.map(({ date, amount }) => ({
          x: getNumberOfDigits(date) < 12 ? date * 1000 : date,
          prediction: amount,
        })),
      ];
    },
    expenditureCategories(): CategoryRowDataProps[] {
      const expenditureCategoriesSnapshot = getSnapshot(
        self.expenditure_categories
      );
      return mapCategories(expenditureCategoriesSnapshot);
    },
    incomeCategories(): CategoryRowDataProps[] {
      const expenditureCategoriesSnapshot = getSnapshot(self.income_categories);
      return mapCategories(expenditureCategoriesSnapshot);
    },
  }));

export type BankAccountsFinancialStatsSnapshot = SnapshotIn<
  typeof BankAccountsFinancialStats
>;
export type BankAccountsFinancialStatsInstance = Instance<
  typeof BankAccountsFinancialStats
>;
